/**
 * Domain view — lists every entity in a domain (route `#domain/<id>`).
 */
import type { AtlasManifest, DomainLayer } from '@atlas-manifest/types';
import { EmptyState } from '../components/EmptyState.js';
import { ObjectHeader } from '../components/ObjectHeader.js';
import { domainIcon, inferEntityShape, ENTITY_SHAPE_ICONS } from '../icons/catalog.js';
import { domainById, entitiesInDomain, computeDomainStats } from '../state/selectors.js';
import { navigate } from '../state/hashRouter.js';
import { ConnectivityPill } from './ConnectivityPill.js';
import { Icon } from '../components/Icon.js';

export interface DomainViewProps {
  manifest: AtlasManifest;
  domainId: string;
}

export function DomainView({ manifest, domainId }: DomainViewProps): React.JSX.Element {
  const domain = domainById(manifest, domainId);
  if (!domain) {
    return (
      <EmptyState icon="help" title={`No domain "${domainId}"`}>
        <button className="btn btn-ghost" onClick={() => navigate({ kind: 'welcome' })}>
          Back to welcome
        </button>
      </EmptyState>
    );
  }
  const entities = entitiesInDomain(manifest, domainId);
  const colorVar = `var(--${domain.color.replace(/^--/, '')})`;
  const stats = computeDomainStats(manifest).find((s) => s.domainId === domainId);

  return (
    <article>
      <ObjectHeader
        object={domain}
        icon={domainIcon(domain.id)}
        iconLabel="domain"
        iconColor={colorVar}
        badges={[
          { label: domain.id, type: 'domain' as const, size: 'md' as const },
          { label: (domain.layer as DomainLayer) ?? 'L5', type: 'layer' as const, size: 'md' as const, showLabel: true },
        ]}
        title={domain.name}
        docRef={domain.docRef}
        description={domain.description}
        chips={domain.packGate ? [{ label: `${domain.packGate} pack`, tone: 'caution' as const }] : []}
      />
      {stats && (
        <div style={{ marginTop: 'var(--sp-md)' }}>
          <ConnectivityPill status={stats.connectivity} />
        </div>
      )}

      <div className="section-label">Entities · {entities.length}</div>
      {entities.length === 0 ? (
        <EmptyState icon="folder_open" title="No entities in this domain" />
      ) : (
        entities.map((entity) => (
          <button
            key={entity.id}
            className="ref-row"
            onClick={() => navigate({ kind: 'entity', id: entity.id })}
          >
            <Icon name={ENTITY_SHAPE_ICONS[inferEntityShape(entity)]} size="sm" />
            <span className="mono">{entity.name}</span>
            <span style={{ color: 'var(--text3)', marginLeft: 'auto' }}>
              {entity.fields.length} fields
              {entity.isPersisted ? ' · persisted' : ' · ephemeral'}
            </span>
          </button>
        ))
      )}
    </article>
  );
}
